import { Fragment, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import useDebouncedValue from '../hooks/useDebouncedValue';

const TABLE_LABELS = {
  assets: 'Asset',
  asset_transfers: 'Transfer',
  damage_reports: 'Damage Report',
};

const ACTION_LABELS = {
  INSERT: { label: 'Created', className: 'badge-green' },
  UPDATE: { label: 'Updated', className: 'badge' },
  DELETE: { label: 'Deleted', className: 'badge-red' },
};

export default function AuditLog() {
  const { withAuth } = useAuth();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [filterTable, setFilterTable] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterAssetType, setFilterAssetType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expanded, setExpanded] = useState(null);
  // A failed load used to leave `entries` empty and say nothing, so the page
  // rendered "No audit entries found" — the same words it uses for a genuinely
  // empty result. A 403 read as "nothing was ever changed", which is the one
  // conclusion an audit log must never invite by accident. It is how the
  // supervisor role mismatch went unnoticed: they were refused and told the log
  // was empty. Holding the failure here is what lets the two be told apart.
  const [error, setError] = useState(null);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await withAuth(async () => {
        const params = { limit, page };
        if (debouncedSearch) params.search = debouncedSearch;
        if (filterTable) params.table = filterTable;
        if (filterAction) params.action = filterAction;
        if (filterAssetType) params.assetType = filterAssetType;
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        const data = await api.get('/audit', params);
        setEntries(data.entries || []);
        setTotal(data.total || 0);
      });
    } catch(e) {
      console.error('Audit log load error:', e);
      setEntries([]);
      setTotal(0);
      setError(e.status === 403
        ? 'You do not have permission to view the audit log.'
        : (e.message || 'Could not load the audit log.'));
    } finally {
      setLoading(false);
    }
  }, [withAuth, page, debouncedSearch, filterTable, filterAction, filterAssetType, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  function getChangeSummary(entry) {
    if (entry.table_name === 'asset_transfers') {
      const nv = entry.new_values || {};
      return `Transferred to ${nv.to_location_id ? 'new location' : 'unassigned'}`;
    }
    if (entry.table_name === 'assets' && entry.action === 'UPDATE') {
      const ov = entry.old_values || {};
      const nv = entry.new_values || {};
      const changes = [];
      if (ov.display_name !== nv.display_name) changes.push(`Name: "${ov.display_name}" → "${nv.display_name}"`);
      if (ov.status !== nv.status) changes.push(`Status: ${ov.status} → ${nv.status}`);
      if (ov.current_location_id !== nv.current_location_id) changes.push('Location changed');
      if (ov.assigned_to_employee_id !== nv.assigned_to_employee_id) changes.push('Assignment changed');
      if (ov.notes !== nv.notes) changes.push('Notes updated');
      return changes.length ? changes.join(' · ') : 'Updated';
    }
    return ACTION_LABELS[entry.action]?.label || entry.action;
  }

  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <div style={{ padding: '20px 28px', borderBottom: '2px solid var(--accent)', background: 'var(--bg-1)' }}>
        <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Audit Log</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input className="input" placeholder="Search..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ width: 200 }} />
          <select className="select" value={filterTable} onChange={e => { setFilterTable(e.target.value); setPage(1); }} style={{ width: 150 }}>
            <option value="">All Tables</option>
            <option value="assets">Assets</option>
            <option value="asset_transfers">Transfers</option>
            <option value="damage_reports">Damage Reports</option>
          </select>
          <select className="select" value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }} style={{ width: 130 }}>
            <option value="">All Actions</option>
            <option value="INSERT">Created</option>
            <option value="UPDATE">Updated</option>
            <option value="DELETE">Deleted</option>
          </select>
          <select className="select" value={filterAssetType} onChange={e => { setFilterAssetType(e.target.value); setPage(1); }} style={{ width: 140 }}>
            <option value="">All Types</option>
            <option value="radio">Radio</option>
            <option value="cell_phone">Cell Phone</option>
            <option value="tablet">Tablet</option>
            <option value="laptop">Laptop</option>
            <option value="desktop">Desktop</option>
          </select>
          <input type="date" className="input" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }} style={{ width: 145 }} />
          <input type="date" className="input" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }} style={{ width: 145 }} />
          {(search || filterTable || filterAction || filterAssetType || startDate || endDate) && (
            <button className="btn btn-sm" onClick={() => { setSearch(''); setFilterTable(''); setFilterAction(''); setFilterAssetType(''); setStartDate(''); setEndDate(''); setPage(1); }}>✕ Clear</button>
          )}
          {/* Suppressed on failure: "0 entries" next to an error is the same
              false claim the empty state used to make. */}
          <span style={{ marginLeft: 'auto', fontSize: 14, color: 'var(--text-2)' }}>
            {error ? '—' : `${total.toLocaleString()} entries`}
          </span>
        </div>
      </div>

      <div style={{ padding: '0 28px 28px' }}>
        {loading ? (
          <div className="empty-state"><div className="spinner" /></div>
        ) : error ? (
          // Distinct from the empty state on purpose — "we could not look" and
          // "there is nothing to see" are opposite facts about an audit log.
          <div className="empty-state">
            <div style={{ fontSize: 24, marginBottom: 10 }}>⚠</div>
            <div style={{ color: 'var(--red)', marginBottom: 4 }}>Could not load the audit log</div>
            <div style={{ marginBottom: 16 }}>{error}</div>
            <button className="btn btn-sm" onClick={load}>Try again</button>
          </div>
        ) : entries.length === 0 ? (
          <div className="empty-state">No audit entries found</div>
        ) : (
          <>
            {/* The only table in the app that wasn't in a card. It went
                unnoticed while headers were a grey hairline; a red header row
                with square corners sitting loose on the page background made it
                obvious. */}
            <div className="card" style={{ marginTop: 20 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Date & Time</th>
                  <th>Type</th>
                  <th>Action</th>
                  <th>Asset</th>
                  <th>Change Summary</th>
                  <th>By</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  // The key belongs on the mapped root. It sat on the <tr>
                  // inside a bare fragment, which React counts as keyless.
                  <Fragment key={e.id}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(expanded === e.id ? null : e.id)}>
                      <td style={{ fontSize: 13, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
                        {new Date(e.changed_at).toLocaleString()}
                      </td>
                      <td><span className="chip">{TABLE_LABELS[e.table_name] || e.table_name}</span></td>
                      <td><span className={`badge ${ACTION_LABELS[e.action]?.className || ''}`}>{ACTION_LABELS[e.action]?.label || e.action}</span></td>
                      <td>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{e.asset_name || '—'}</div>
                        {e.asset_tag && <div style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--mono)' }}>{e.asset_tag}</div>}
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--text-2)', maxWidth: 300 }}>{getChangeSummary(e)}</td>
                      <td style={{ fontSize: 13 }}>{e.changed_by_name || '—'}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{expanded === e.id ? '▲' : '▼'}</td>
                    </tr>
                    {expanded === e.id && (
                      <tr>
                        <td colSpan={7} style={{ background: 'var(--bg-2)', padding: '12px 20px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, fontSize: 13 }}>
                            {e.old_values && (
                              <div>
                                <div style={{ fontWeight: 500, marginBottom: 6, color: 'var(--text-1)' }}>Before</div>
                                <pre style={{ fontSize: 12, color: 'var(--text-2)', whiteSpace: 'pre-wrap', margin: 0 }}>
                                  {JSON.stringify(e.old_values, null, 2)}
                                </pre>
                              </div>
                            )}
                            {e.new_values && (
                              <div>
                                <div style={{ fontWeight: 500, marginBottom: 6, color: 'var(--text-1)' }}>After</div>
                                <pre style={{ fontSize: 12, color: 'var(--text-2)', whiteSpace: 'pre-wrap', margin: 0 }}>
                                  {JSON.stringify(e.new_values, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20 }}>
                <button className="btn btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                <span style={{ fontSize: 14, padding: '6px 12px', color: 'var(--text-2)' }}>Page {page} of {totalPages}</span>
                <button className="btn btn-sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
